import React, { useEffect, useState } from "react";
import SeoHead from "./SiteSeo";
import SiteLayout from "./SiteLayout";
import { SmartLink } from "./SiteHeader";
import { FinalCta } from "./sections";
import { publicApi } from "../pages/public/api";

const fmtDay = (iso) => new Date(iso).toLocaleDateString("en-CA", { month: "short", day: "numeric" });
const DIRW = { increase: "Up", decrease: "Down", hold: "Hold" };

// /rates/track-record — every forecast call graded against what lenders actually did. Only indexable once there's a real sample.
export default function TrackRecordPage() {
  const [data, setData] = useState(null);
  useEffect(() => { publicApi.get("/track-record").then((r) => setData(r.data)).catch(() => setData({ graded: 0 })); }, []);
  const enough = (data?.graded || 0) >= 10;
  return (
    <SiteLayout>
      <SeoHead title="Our Rate Forecast Track Record | Hello Mortgage Team" description="Every call we make on where mortgage rates are heading gets graded against what lenders actually did. Here's the scorecard—hits, misses and all." path="/rates/track-record/" />
      <section className="hero solution-hero" data-testid="track-record-hero">
        <div className="hero-grid wrap solution-hero-grid"><div>
          <div className="eyebrow">Rate intelligence · the scorecard</div>
          <h1 style={{ fontSize: "clamp(2.6rem,4.8vw,4.2rem)" }}>We grade our own homework.</h1>
          <p>Every time we call a rate move, we write it down. When a lender actually moves, we check the call. No cherry-picking—misses stay on the board.</p>
        </div></div>
      </section>
      <section className="rt-tr" data-testid="track-record">
        <div className="wrap">
          {!data ? <p className="micro">Loading the scorecard…</p> : !enough ? (
            <div className="rt-tr-empty" data-testid="track-record-empty">
              <h2 className="display">The board's still filling in.</h2>
              <p>We publish the scorecard once there are at least ten graded calls in the last 90 days. Until then, the live forecast is on the <SmartLink href="/rates/">rates page</SmartLink>—and every call made there is being graded right now.</p>
            </div>
          ) : (
            <>
              <div className="rt-tr-stats">
                <div><b data-testid="track-record-accuracy">{data.accuracy_pct}%</b><span>right on direction</span></div>
                <div><b>{data.graded}</b><span>calls graded · last {data.window_days} days</span></div>
                <div><b>{data.hits}</b><span>hits</span></div>
              </div>
              <div className="rt-market-table" role="table">
                <div className="rt-market-head" role="row"><span>When</span><span>Lender · term</span><span>We called</span><span>They did</span><span>Result</span></div>
                {data.recent.map((r, i) => (
                  <div className="rt-market-row" role="row" key={i}>
                    <span>{fmtDay(r.event_at)}</span>
                    <span>{r.lender_name} <small>{r.term}</small></span>
                    <span>{DIRW[r.predicted_direction] || r.predicted_direction}{r.predicted_bps_range ? <small> {r.predicted_bps_range}</small> : null}</span>
                    <span>{DIRW[r.actual_direction] || r.actual_direction}{r.actual_change_bps != null ? <small> {r.actual_change_bps > 0 ? "+" : ""}{(r.actual_change_bps / 100).toFixed(2)}%</small> : null}</span>
                    <span className={r.hit ? "up" : "down"}>{r.hit ? "Hit" : "Miss"}</span>
                  </div>
                ))}
              </div>
              <p className="micro">A "hit" means we called the direction of the move before the lender made it. Sizes are ranges, not points—we grade direction, not decimals.</p>
            </>
          )}
        </div>
      </section>
      <FinalCta />
    </SiteLayout>
  );
}
